import type { Channel, ConversationEntry, CustomerRecord } from '@/domain/types';
import { depositLinkRoute, type DepositLinkRoute } from '@/domain/depositLinkSend';
import { isStoreFallbackEnabled } from '@/api/apiMode';
import { sendDmIntent } from '@/api/intents';
import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { useComposerDraftStore } from '@/state/composerDraftStore';
import { withServerAuthoredWrites } from '@/state/persistenceBridge';
import { customerName } from '@/utils/format';

/**
 * The route a deposit message takes (tv-isee), with the same store-fallback gate
 * CustomerComposer uses: tests and standalone mode stay local. `localOnly` is the
 * caller's call (a pay-over-time plan, a pay-at-studio hold). `nowIso` MUST be wall time.
 */
export function depositMessageRoute(a: {
  customer: Pick<CustomerRecord, 'conversation'>;
  channel: Channel;
  nowIso: string;
  localOnly?: boolean;
}): DepositLinkRoute {
  if (isStoreFallbackEnabled()) return 'local';
  return depositLinkRoute({ customer: a.customer, channel: a.channel, planEnabled: false, holdOnly: !!a.localOnly, nowIso: a.nowIso });
}

/** What the message is, for the toast wording: the first link (holds a slot), a fresh link, or a nudge. */
export type DepositMessageKind = 'link' | 'resend' | 'nudge';

/** The template each kind is tagged with on the send and on a message-box draft. */
const TEMPLATE_ID: Record<DepositMessageKind, string> = { link: 'deposit-link', resend: 'deposit-resend', nudge: 'deposit-nudge' };

const COPY: Record<DepositMessageKind, { sent: string; failed: string; drafted: string }> = {
  link: { sent: 'Deposit link sent to Instagram', failed: "Slot held, but the link didn't reach Instagram", drafted: 'Slot held. The deposit message is in the message box' },
  resend: { sent: 'Fresh link sent to Instagram', failed: "The fresh link didn't reach Instagram", drafted: 'The fresh link is in the message box' },
  nudge: { sent: 'Nudge sent to Instagram', failed: "The nudge didn't reach Instagram", drafted: 'The nudge is in the message box' },
};

/**
 * Delivers a deposit message (tv-isee), shared by the deposit modal, resend and nudge.
 * `depositId` ties the message to its deposit card; a nudge passes none, so it shows as
 * a plain message rather than a second card for the same deposit.
 * - 'instagram': sends on the normal reply path, NOT awaited by the caller. On ok the
 *   server entry replaces the local note; if the request fails the local note is written,
 *   marked not delivered, so the link is still in the thread.
 * - 'window-closed': hands the message to the customer's message box as a draft carrying
 *   the deposit id; the agent sends it from there. No local note. An automatic caller
 *   passes `handoff: false`: nobody is there to send the draft, and it would overwrite the
 *   agent's, so it writes the local note instead and returns 'local'.
 * - 'local': writes the local note. The caller pushes its own toast.
 * Returns the route it took.
 */
export function deliverDepositMessage(a: {
  customer: CustomerRecord;
  channel: Channel;
  text: string;
  depositId?: string;
  localNote: ConversationEntry;
  nowIso: string;
  localOnly?: boolean;
  kind?: DepositMessageKind;
  /** False for automatic callers: never drop a draft into the message box. Default true. */
  handoff?: boolean;
}): DepositLinkRoute {
  const routed = depositMessageRoute(a);
  const route = routed === 'window-closed' && a.handoff === false ? 'local' : routed;
  const notDelivered = (n: ConversationEntry): ConversationEntry => ({ ...n, body: `${n.body} · Not delivered on Instagram. Paste the link into the chat.` });
  const copy = COPY[a.kind ?? 'link'];
  const templateId = TEMPLATE_ID[a.kind ?? 'link'];
  const { addConversationEntry } = useBrainStore.getState();
  const { pushNotification } = useUIStore.getState();
  const customerId = a.customer.id;

  if (route === 'window-closed') {
    useComposerDraftStore.getState().setDraft(customerId, a.text, templateId, a.depositId);
    pushNotification({ intensity: 'info', title: copy.drafted, body: 'Check it and send.', ttlMs: 6000 });
  } else if (route === 'instagram') {
    void sendDmIntent({ clientMessageId: crypto.randomUUID(), customerId, text: a.text, path: 'reply', templateId, ...(a.depositId ? { depositId: a.depositId } : {}) }).then((res) => {
      if (res.ok) {
        const already = useBrainStore.getState().customers[customerId]?.conversation.some((e) => e.id === res.entry.id);
        if (!already) withServerAuthoredWrites([['customers', customerId]], () => addConversationEntry(customerId, res.entry));
        pushNotification(res.deliveryStatus === 'failed'
          ? { intensity: 'high', title: copy.failed, body: 'Use Try again on the message.', ttlMs: 6000 }
          : { intensity: 'info', title: copy.sent, body: customerName(a.customer), ttlMs: 4000 });
      } else {
        addConversationEntry(customerId, notDelivered(a.localNote));
        pushNotification({ intensity: 'high', title: copy.failed, body: 'Paste the link into your Instagram chat.', ttlMs: 6000 });
      }
    });
  } else {
    // An automatic caller outside 24h (handoff off) also reaches nobody on Instagram.
    addConversationEntry(customerId, routed === 'window-closed' ? notDelivered(a.localNote) : a.localNote);
  }
  return route;
}
